<script setup>
import {
  computed,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import CampaignCenterAPI from '../api';
import { CAMPAIGN_STATUSES, isRunning } from '../helpers';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import CampaignListItem from '../components/CampaignListItem.vue';

const props = defineProps({
  channel: { type: String, required: true }, // whatsapp | sms
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const whatsappInboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const smsInboxes = useMapGetter('inboxes/getSMSInboxes');
const channelInboxes = computed(
  () =>
    (props.channel === 'sms' ? smsInboxes.value : whatsappInboxes.value) || []
);

const campaigns = ref([]);
const total = ref(0);
const perPage = ref(25);
const page = ref(1);
const statusCounts = ref({});
const status = ref(route.query.status || 'all');
const search = ref('');
const inboxId = ref('');
const sort = ref('newest');
const isLoading = ref(false);
const loaded = ref(false);
const deleteTarget = ref(null);
const deleteDialogRef = ref(null);
let pollTimer = null;

const POLL_INTERVAL = 10000;

const load = async ({ silent = false } = {}) => {
  if (!silent) isLoading.value = true;
  try {
    const { data } = await CampaignCenterAPI.list({
      channel: props.channel,
      status: status.value === 'all' ? undefined : status.value,
      q: search.value || undefined,
      inbox_id: inboxId.value || undefined,
      sort: sort.value,
      page: page.value,
    });
    campaigns.value = data.payload;
    total.value = data.meta.total;
    perPage.value = data.meta.per_page;
    statusCounts.value = data.meta.status_counts || {};
    loaded.value = true;
  } finally {
    isLoading.value = false;
  }
};
const debouncedLoad = debounce(() => load(), 400, false);

// moderate polling only while something is running on this page
const schedulePoll = () => {
  clearInterval(pollTimer);
  pollTimer = setInterval(() => {
    if (campaigns.value.some(c => isRunning(c.status))) load({ silent: true });
  }, POLL_INTERVAL);
};

onActivated(() => {
  load();
  schedulePoll();
});
onDeactivated(() => clearInterval(pollTimer));
onBeforeUnmount(() => clearInterval(pollTimer));

watch(
  () => props.channel,
  () => {
    page.value = 1;
    status.value = 'all';
    inboxId.value = '';
    load();
  }
);
watch([status, inboxId, sort], () => {
  page.value = 1;
  load();
});
watch(search, () => {
  page.value = 1;
  debouncedLoad();
});
watch(page, () => load());

const tabs = computed(() =>
  ['all', ...CAMPAIGN_STATUSES].map(key => ({
    key,
    label: t(`WASAPI_CAMPAIGNS.TABS.${key}`),
    count: statusCounts.value[key] || 0,
  }))
);
const inboxOptions = computed(() => [
  { value: '', label: t('WASAPI_CAMPAIGNS.INBOX_ALL') },
  ...channelInboxes.value.map(inbox => ({
    value: String(inbox.id),
    label: inbox.name,
  })),
]);
const sortOptions = computed(() =>
  ['newest', 'oldest', 'scheduled'].map(value => ({
    value,
    label: t(`WASAPI_CAMPAIGNS.SORT.${value}`),
  }))
);
const hasFilters = computed(
  () => status.value !== 'all' || !!search.value || !!inboxId.value
);
const noInbox = computed(
  () => loaded.value && channelInboxes.value.length === 0
);
const channelLabel = computed(() =>
  t(`WASAPI_CAMPAIGNS.CHANNEL.${props.channel}`)
);

const openCampaign = (campaign, tab = 'overview') =>
  router.push({
    name: 'wasapi_campaign_detail',
    params: { channel: props.channel, campaignId: campaign.id },
    query: tab === 'overview' ? {} : { tab },
  });
const editCampaign = campaign =>
  router.push({
    name: 'wasapi_campaign_edit',
    params: { channel: props.channel, campaignId: campaign.id },
  });
const newCampaign = () =>
  router.push({
    name: 'wasapi_campaign_new',
    params: { channel: props.channel },
  });
const openSettings = () => router.push({ name: 'wasapi_campaign_settings' });
const goToInboxes = () => router.push({ name: 'settings_inbox_new' });

const showError = error =>
  useAlert(error?.response?.data?.error || t('WASAPI_CAMPAIGNS.TOAST.ERROR'));

const duplicate = async campaign => {
  try {
    const { data } = await CampaignCenterAPI.duplicate(campaign.id);
    useAlert(t('WASAPI_CAMPAIGNS.TOAST.DUPLICATED'));
    router.push({
      name: 'wasapi_campaign_edit',
      params: { channel: props.channel, campaignId: data.id },
    });
  } catch (error) {
    showError(error);
  }
};
const pause = async campaign => {
  try {
    await CampaignCenterAPI.pause(campaign.id);
    useAlert(t('WASAPI_CAMPAIGNS.TOAST.PAUSED'));
    load({ silent: true });
  } catch (error) {
    showError(error);
  }
};
const resume = async campaign => {
  try {
    await CampaignCenterAPI.resume(campaign.id);
    useAlert(t('WASAPI_CAMPAIGNS.TOAST.RESUMED'));
    load({ silent: true });
  } catch (error) {
    showError(error);
  }
};
const askDelete = campaign => {
  deleteTarget.value = campaign;
  deleteDialogRef.value.open();
};
const confirmDelete = async () => {
  try {
    await CampaignCenterAPI.destroy(deleteTarget.value.id);
    useAlert(t('WASAPI_CAMPAIGNS.TOAST.DELETED'));
    deleteDialogRef.value.close();
    load({ silent: true });
  } catch (error) {
    showError(error);
  }
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-y-auto bg-n-surface-1">
    <div class="flex flex-col w-full max-w-6xl gap-5 px-6 py-6 mx-auto">
      <header class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex flex-col gap-1">
          <h1 class="text-xl font-medium text-n-slate-12">
            {{
              channel === 'sms'
                ? t('WASAPI_CAMPAIGNS.TITLE_SMS')
                : t('WASAPI_CAMPAIGNS.TITLE_WHATSAPP')
            }}
          </h1>
          <p class="text-sm text-n-slate-11">
            {{ t('WASAPI_CAMPAIGNS.DESCRIPTION') }}
          </p>
        </div>
        <div class="flex items-center gap-2">
          <Button
            :label="t('WASAPI_CAMPAIGNS.SETTINGS_LINK')"
            icon="i-lucide-shield-ban"
            variant="faded"
            color="slate"
            size="sm"
            @click="openSettings"
          />
          <Button
            :label="t('WASAPI_CAMPAIGNS.NEW')"
            icon="i-lucide-plus"
            color="blue"
            size="sm"
            :disabled="noInbox"
            @click="newCampaign"
          />
        </div>
      </header>

      <div class="flex flex-wrap items-center gap-1.5">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors"
          :class="
            status === tab.key
              ? 'bg-n-solid-3 text-n-slate-12 font-medium'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="status = tab.key"
        >
          {{ tab.label }}
          <span class="px-1.5 text-xs rounded-md bg-n-alpha-2 tabular-nums">{{
            tab.count
          }}</span>
        </button>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <Input
          v-model="search"
          type="search"
          :placeholder="t('WASAPI_CAMPAIGNS.SEARCH_PLACEHOLDER')"
          class="w-72"
        />
        <div class="w-56 shrink-0">
          <ComboBox
            v-if="channelInboxes.length > 1"
            v-model="inboxId"
            :options="inboxOptions"
          />
        </div>
        <span class="flex-1" />
        <div class="w-44 shrink-0">
          <ComboBox v-model="sort" :options="sortOptions" class="w-44" />
        </div>
      </div>

      <div
        v-if="isLoading && !campaigns.length"
        class="flex justify-center py-16"
      >
        <Spinner />
      </div>

      <div
        v-else-if="noInbox"
        class="flex flex-col items-center gap-3 py-16 text-center rounded-xl outline outline-1 outline-dashed outline-n-weak"
      >
        <span class="i-lucide-inbox size-8 text-n-slate-10" />
        <h2 class="text-base font-medium text-n-slate-12">
          {{
            t('WASAPI_CAMPAIGNS.EMPTY.NO_INBOX_TITLE', {
              channel: channelLabel,
            })
          }}
        </h2>
        <p class="max-w-md text-sm text-n-slate-11">
          {{
            t('WASAPI_CAMPAIGNS.EMPTY.NO_INBOX_SUBTITLE', {
              channel: channelLabel,
            })
          }}
        </p>
        <Button
          :label="t('WASAPI_CAMPAIGNS.EMPTY.GO_TO_INBOXES')"
          size="sm"
          variant="faded"
          color="slate"
          @click="goToInboxes"
        />
      </div>

      <div
        v-else-if="!campaigns.length"
        class="flex flex-col items-center gap-3 py-16 text-center rounded-xl outline outline-1 outline-dashed outline-n-weak"
      >
        <span class="i-lucide-megaphone size-8 text-n-slate-10" />
        <template v-if="hasFilters">
          <p class="text-sm text-n-slate-11">
            {{ t('WASAPI_CAMPAIGNS.EMPTY.NO_RESULTS') }}
          </p>
        </template>
        <template v-else>
          <h2 class="text-base font-medium text-n-slate-12">
            {{ t('WASAPI_CAMPAIGNS.EMPTY.TITLE') }}
          </h2>
          <p class="max-w-md text-sm text-n-slate-11">
            {{ t('WASAPI_CAMPAIGNS.EMPTY.SUBTITLE') }}
          </p>
          <Button
            :label="t('WASAPI_CAMPAIGNS.NEW')"
            icon="i-lucide-plus"
            color="blue"
            size="sm"
            @click="newCampaign"
          />
        </template>
      </div>

      <div v-else class="flex flex-col gap-3">
        <CampaignListItem
          v-for="campaign in campaigns"
          :key="campaign.id"
          :campaign="campaign"
          @open="
            tab =>
              openCampaign(campaign, typeof tab === 'string' ? tab : 'overview')
          "
          @edit="editCampaign(campaign)"
          @duplicate="duplicate(campaign)"
          @delete="askDelete(campaign)"
          @pause="pause(campaign)"
          @resume="resume(campaign)"
        />
        <PaginationFooter
          v-if="total > perPage"
          :current-page="page"
          :total-items="total"
          :items-per-page="perPage"
          @update:current-page="page = $event"
        />
      </div>
    </div>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('WASAPI_CAMPAIGNS.CONFIRM.DELETE_TITLE')"
      :description="
        t('WASAPI_CAMPAIGNS.CONFIRM.DELETE_DESC', {
          title: deleteTarget?.title || '',
        })
      "
      :confirm-button-label="t('WASAPI_CAMPAIGNS.ACTIONS.DELETE')"
      :cancel-button-label="t('WASAPI_CAMPAIGNS.CONFIRM.CANCEL')"
      @confirm="confirmDelete"
    />
  </section>
</template>
